// /Users/aminajackson/Git-projects/pro-maximizer/frontend/src/App.tsx

import React, { useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Alert,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Toolbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  alpha,
  Select,
  FormControl,
  InputLabel,
  Stack,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  OutlinedInput,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Home as HomeIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Dashboard as DashboardIcon,
  ViewKanban as ViewKanbanIcon,
  Description as DescriptionIcon,
  RocketLaunch as RocketLaunchIcon,
  Support as SupportIcon,
  MoreHoriz as MoreHorizIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  MoreVert as MoreVertIcon,
  FormatListBulleted as FormatListBulletedIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  TrackChanges as TrackChangesIcon,
  Person as PersonIcon,
  ArrowDropDown as ArrowDropDownIcon,
  RecentActors as RecentActorsIcon,
  Favorite as FavoriteIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  AddLink as AddLinkIcon,
  GitHub as GitHubIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

// Correctly import from the new central context file
import { AppProvider, useAppContext } from './AppContext';

import Sidebar from './Sidebar';
import ProjectsList from './ProjectsList';
import CollabSpace, { KnowledgeItem } from './CollabSpace';
import Develop, { DevelopState } from './Develop';
import NewProject from './NewProject';
import SettingsPage from './SettingsPage';
import BoardsPage from './BoardsPage';
import RiskRadar, { Risk } from './RiskRadar';
import { API_BASE_URL } from './api';

// --- Type Definitions ---
export type ProjectStatus = 'Under Review' | 'Approved' | 'Reevaluation requested' | 'Executed' | 'Hold' | 'Rejected';
export type ProjectTarget = 'Outsource contract' | 'Staffing' | 'Leadership signoff';

export interface PlanComponent {
  id: string;
  name: string;
  status: ProjectStatus;
  target: ProjectTarget;
  assignee: string;
}

export interface Project {
  id: number; // Corrected: Was string, causing find operations to fail.
  name: string;
  planData: any; // This will hold the transformed plan_data from the backend
  manager: string;
  team: string[];
  startDate: string;
  endDate: string;
  connectedBoard?: string;
  connectedGithub?: string;
  connectedStorage?: string;
  connectedDatabase?: string;
  connectedCloudLogging?: string;
  connectedAzureMonitor?: string;
  connectedCloudWatch?: string;
  planComponents: PlanComponent[];
  location: string;
  createdBy: string;
  dateViewed: string;
}

export interface ProjectIdentifier {
  id: number;
  name: string;
}


const drawerWidth = 240;
const DEFAULT_COMPANY_NAME = 'IntechGambit';
const DEFAULT_TEAM_NAME = 'Pro-Maximizer';

type ResourceType = 'board' | 'github' | 'storage' | 'database' | 'cloudLogging' | 'azureMonitor' | 'cloudWatch';

const getFromSession = <T,>(key: string, defaultValue: T): T => {
  try {
    const storedValue = sessionStorage.getItem(key);
    if (storedValue) {
      return JSON.parse(storedValue);
    }
  } catch (error) {
    console.error(`Error reading from sessionStorage for key "${key}":`, error);
  }
  return defaultValue;
};

const saveToSession = <T,>(key: string, value: T) => {
  try {
    if (value === null || value === undefined) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, JSON.stringify(value));
    }
  } catch (error) {
    console.error(`Error writing to sessionStorage for key "${key}":`, error);
  }
};


const planFeatureMap: { [key: string]: string } = {
    projectSummary: 'Project Summary', // Added for completeness
    readiness: 'Readiness Assessment',
    sow: 'Statement of Work',
    optimization: 'Optimization & scaling',
    milestones: 'Milestone Generation',
    quality: 'Product Quality',
    roles: 'Role & Skill Recommendations',
    scenarios: 'Scenario Models',
};

/**
 * This component contains the main application layout and logic.
 */
const AppContent = () => {
  const { companyName, teamName, addActivity, mode, toggleColorMode, addTeamMembers, teamMembers } = useAppContext();

  // --- State Management ---
  const [selectedSidebarItem, setSelectedSidebarItem] = useState<string>('home');
  const [isAppReady, setIsAppReady] = useState(false);
  const [risks, setRisks] = useState<Risk[]>([]);
  const [userProjects, setUserProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [projectList, setProjectList] = useState<ProjectIdentifier[]>([]);


  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isInviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [activePlanTab, setActivePlanTab] = useState<string>('summary');
  const [showHomePlanBoard, setShowHomePlanBoard] = useState<boolean>(false);
  const [knowledgeItems, setKnowledgeItems] = useState<KnowledgeItem[]>([]);
  const [userName, setUserName] = useState<string>('Guest User');

  const [newProjectMode, setNewProjectMode] = useState<string>(getFromSession('newProjectMode', ''));
  const [totalAutomationResult, setTotalAutomationResult] = useState<any>(getFromSession('totalAutomationResult', null));
  const [generatedProjectName, setGeneratedProjectName] = useState<string>(getFromSession('generatedProjectName', ''));

  const [developState, setDevelopState] = useState<DevelopState>(
    getFromSession('developState', {
      repoUrl: '', githubToken: '', repoData: null, commits: [], pulls: [], issues: [], commitAnalysis: '', error: null, isLoading: false,
    })
  );

  const [isPlanEditing, setIsPlanEditing] = useState(false);
  const [editedPlanComponents, setEditedPlanComponents] = useState<PlanComponent[] | null>(null);

  const [isAddResourceDialogOpen, setAddResourceDialogOpen] = useState(false);
  const [isRemoveResourceDialogOpen, setRemoveResourceDialogOpen] = useState(false);
  const [resourceTypeToAdd, setResourceTypeToAdd] = useState<ResourceType | ''>('');
  const [resourceValueToAdd, setResourceValueToAdd] = useState('');
  const [resourceToRemove, setResourceToRemove] = useState<ResourceType | ''>('');

  const selectedProjectData = useMemo(() => userProjects.find(p => p.id === selectedProject), [userProjects, selectedProject]);

  // --- Data Fetching and Callbacks ---

  const fetchProjectIdentifiers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/projects/identifiers`);
      if (!response.ok) {
        throw new Error('Failed to fetch project list');
      }
      const data: ProjectIdentifier[] = await response.json();
      setProjectList(data);
    } catch (error) {
      console.error("Error fetching project identifiers:", error);
      setMessage({ text: 'Could not load project list.', severity: 'error' });
    }
  }, []);


  const fetchProjects = useCallback(async () => {
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/projects`);
      if (!response.ok) {
        throw new Error('Failed to fetch projects from the server.');
      }
      const data = await response.json();

      // FIX: Transform backend data to match the frontend's expected Project interface.
      // This was a major source of issues.
      const transformedData = data.map((project: any): Project => {
        const planData = project.plan_data || {};

        // Dynamically create planComponents from the fetched data. This is the key fix for the Plan page.
        const planComponents: PlanComponent[] = Object.keys(planFeatureMap)
            .filter(key => planData[key] != null && planData[key] !== '') // Check for non-null, non-empty values
            .map((key, index) => ({
                id: `pc-${project.id}-${index}`, // Create a unique ID
                name: planFeatureMap[key as keyof typeof planFeatureMap],
                // Use default values, as status/assignee aren't stored per-component in the DB
                status: 'Approved',
                target: 'Staffing',
                assignee: project.created_by || 'N/A',
            }));

        return {
          id: project.id,
          name: project.name,
          planData: planData,
          manager: project.created_by || 'N/A',
          team: [], // Default value, as this isn't stored in the main project table
          startDate: new Date().toISOString().split('T')[0], // Default value
          endDate: new Date().toISOString().split('T')[0], // Default value
          connectedBoard: project.board ? project.board.name : undefined,
          planComponents: planComponents, // Use the newly generated components
          location: 'Cloud', // Default value
          createdBy: project.created_by || 'N/A',
          dateViewed: project.created_at,
        };
      });
      setUserProjects(transformedData);
    } catch (error: any) {
      setProjectsError(error.message);
      console.error("Error fetching projects:", error);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  const fetchKnowledgeItems = useCallback(async (type?: string, search?: string) => {
    const mockItems: KnowledgeItem[] = [
        { id: 'ki-1', title: 'How to Deploy to Production', type: 'How-to Guides', tags: ['deployment', 'prod'], content: '1. Run tests. 2. Build the container. 3. Push to registry.', createdBy: 'Amina Jackson', createdAt: new Date().toISOString() },
        { id: 'ki-2', title: 'Database Connection Timeout', type: 'Common Issue & Solution', tags: ['database', 'bug'], content: 'Increase the pool size in the configuration file.', createdBy: 'Leo Vinci', createdAt: new Date(Date.now() - 86400000).toISOString() },
    ];
    let filteredItems = mockItems;
    if (type) {
        filteredItems = filteredItems.filter(item => item.type === type);
    }
    if (search) {
        filteredItems = filteredItems.filter(item => item.title.toLowerCase().includes(search.toLowerCase()) || item.content.toLowerCase().includes(search.toLowerCase()));
    }
    setKnowledgeItems(filteredItems);
  }, []);

  const fetchRisks = useCallback(async () => {
    const mockRisks: Risk[] = [
        { id: 1, type: 'Timeline', message: 'Scope Creep: Uncontrolled changes are extending the project timeline.', severity: 'error' },
        { id: 2, type: 'Stagnation', message: 'Key Staff Unavailability: Risk of project slowdown due to team member absence.', severity: 'warning' },
        { id: 3, type: 'Quality', message: 'Third-party API Failure: Potential for service disruption from external dependencies.', severity: 'info' },
    ];
    setRisks(mockRisks);
  }, []);

  useEffect(() => {
    const initializeAppData = async () => {
      await Promise.all([fetchProjectIdentifiers(), fetchProjects(), fetchKnowledgeItems(), fetchRisks()]);
      setIsAppReady(true);
    };
    initializeAppData();
  }, [fetchProjectIdentifiers, fetchProjects, fetchKnowledgeItems, fetchRisks]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => { saveToSession('newProjectMode', newProjectMode); }, [newProjectMode]);
  useEffect(() => { saveToSession('totalAutomationResult', totalAutomationResult); }, [totalAutomationResult]);
  useEffect(() => { saveToSession('generatedProjectName', generatedProjectName); }, [generatedProjectName]);
  useEffect(() => { saveToSession('developState', developState); }, [developState]);

  const resetNewProjectState = () => {
    setNewProjectMode('');
    setTotalAutomationResult(null);
    setGeneratedProjectName('');
  };

  const resetDevelopState = () => {
    setDevelopState({
        repoUrl: '', githubToken: '', repoData: null, commits: [], pulls: [], issues: [], commitAnalysis: '', error: null, isLoading: false,
    });
  };

  const handleCreateProject = useCallback(async (newlyCreatedProject: Project) => {
    if (!newlyCreatedProject || !newlyCreatedProject.id) {
        setMessage({ text: 'Error: Invalid project data received from server.', severity: 'error' });
        return;
    }
    if (userProjects.some(p => p.id === newlyCreatedProject.id)) {
      setMessage({ text: `Error: Project ID "${newlyCreatedProject.id}" already exists.`, severity: 'error' });
      return;
    }

    const planComponents: PlanComponent[] = Object.keys(planFeatureMap)
        .filter(key => newlyCreatedProject.planData && newlyCreatedProject.planData[key as keyof typeof planFeatureMap])
        .map((key, index) => ({
            id: `pc-${newlyCreatedProject.id}-${index}`,
            name: planFeatureMap[key as keyof typeof planFeatureMap],
            status: 'Under Review',
            target: 'Staffing',
            assignee: newlyCreatedProject.manager || (teamMembers[0] || 'unassigned'),
        }));

    const finalProjectObject: Project = {
      ...newlyCreatedProject,
      planComponents: planComponents,
      location: 'Cloud',
      createdBy: newlyCreatedProject.manager,
      dateViewed: new Date().toISOString().split('T')[0],
    };

    setUserProjects(prev => [...prev, finalProjectObject]);
    setProjectList(prev => [...prev, { id: Number(finalProjectObject.id), name: finalProjectObject.name }]);

    addActivity({ message: `Created project: ${finalProjectObject.name}`, icon: 'Description' });
    setMessage({ text: `Project "${finalProjectObject.name}" was created successfully.`, severity: 'success' });
    setSelectedProject(Number(finalProjectObject.id));
    setSelectedSidebarItem('home');
    resetNewProjectState();
  }, [userProjects, addActivity, teamMembers]);


  const handleDeleteProject = useCallback(async (projectId: number) => {
    setUserProjects(prev => prev.filter(p => p.id !== projectId));
    const newList = projectList.filter(p => p.id !== projectId);
    setProjectList(newList);
    if (selectedProject === projectId) {
        setSelectedProject(null);
    }
    addActivity({ message: `Deleted project ID: ${projectId}`, icon: 'Delete' });
    setMessage({ text: 'Project deleted successfully.', severity: 'info' });
  }, [selectedProject, projectList, addActivity]);

  const handleModifyProject = useCallback(async (project: Project) => {
    setUserProjects(prev => prev.map(p => p.id === project.id ? project : p));
    setProjectList(prev => prev.map(p => p.id === Number(project.id) ? { ...p, name: project.name } : p));
    addActivity({ message: `Modified project: ${project.name}`, icon: 'Edit' });
  }, [addActivity]);

  const handleAddKnowledgeItem = useCallback(async (newItem: Omit<KnowledgeItem, 'id' | 'createdAt' | 'createdBy'>): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/knowledge_items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newItem, createdBy: userName }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add knowledge item');
      }

      addActivity({ message: `Added knowledge item: ${newItem.title}`, icon: 'Group' });
      setMessage({ text: 'Knowledge item added successfully!', severity: 'success' });
      await fetchKnowledgeItems(); // Re-fetch to update the list
      return { success: true };
    } catch (e: any) {
      console.error("Error adding knowledge item:", e);
      setMessage({ text: `Error: ${e.message}`, severity: 'error' });
      return { success: false, error: e.message };
    }
  }, [fetchKnowledgeItems, userName, addActivity]);

  const handleProjectSelectClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleProjectSelectClose = () => setAnchorEl(null);

  const handleProjectMenuItemClick = (projectId: number) => {
    setSelectedProject(projectId);
    setAnchorEl(null);
    setShowHomePlanBoard(false);
  };

  const handleInviteSend = () => {
    const emails = inviteEmails.split(',').map(e => e.trim()).filter(e => e);
    if (emails.length > 0) {
        addTeamMembers(emails);
        setInviteEmails('');
        setInviteDialogOpen(false);
        setMessage({ text: 'Team members invited successfully!', severity: 'success' });
    }
  };

  const handleEditPlan = () => {
    if (selectedProjectData) {
      setEditedPlanComponents(JSON.parse(JSON.stringify(selectedProjectData.planComponents)));
      setIsPlanEditing(true);
    }
  };

  const handleCancelPlanEdit = () => {
    setEditedPlanComponents(null);
    setIsPlanEditing(false);
  };

  const handlePlanComponentChange = (index: number, field: keyof PlanComponent, value: string) => {
    if (editedPlanComponents) {
      const newComponents = [...editedPlanComponents];
      // @ts-ignore
      newComponents[index][field] = value;
      setEditedPlanComponents(newComponents);
    }
  };

  const handleSavePlan = () => {
    if (selectedProjectData && editedPlanComponents) {
      handleModifyProject({ ...selectedProjectData, planComponents: editedPlanComponents });
    }
    handleCancelPlanEdit();
  };

  const handleOpenRemoveDialog = (resourceType: ResourceType) => {
    setResourceToRemove(resourceType);
    setRemoveResourceDialogOpen(true);
  };

  const handleConfirmRemoveResource = () => {
    if (!selectedProjectData || !resourceToRemove) return;
    const updatedProject = { ...selectedProjectData };
    if (resourceToRemove === 'board') updatedProject.connectedBoard = undefined;
    else if (resourceToRemove === 'github') updatedProject.connectedGithub = undefined;
    else if (resourceToRemove === 'storage') updatedProject.connectedStorage = undefined;
    else if (resourceToRemove === 'database') updatedProject.connectedDatabase = undefined;
    else if (resourceToRemove === 'cloudLogging') updatedProject.connectedCloudLogging = undefined;
    else if (resourceToRemove === 'azureMonitor') updatedProject.connectedAzureMonitor = undefined;
    else if (resourceToRemove === 'cloudWatch') updatedProject.connectedCloudWatch = undefined;
    handleModifyProject(updatedProject);
    setRemoveResourceDialogOpen(false);
    setResourceToRemove('');
  };

  const handleSaveResource = () => {
    if (!selectedProjectData || !resourceTypeToAdd || !resourceValueToAdd) return;
    const updatedProject = { ...selectedProjectData };
    if (resourceTypeToAdd === 'board') updatedProject.connectedBoard = resourceValueToAdd;
    else if (resourceTypeToAdd === 'github') updatedProject.connectedGithub = resourceValueToAdd;
    else if (resourceTypeToAdd === 'storage') updatedProject.connectedStorage = resourceValueToAdd;
    else if (resourceTypeToAdd === 'database') updatedProject.connectedDatabase = resourceValueToAdd;
    else if (resourceTypeToAdd === 'cloudLogging') updatedProject.connectedCloudLogging = resourceValueToAdd;
    else if (resourceTypeToAdd === 'azureMonitor') updatedProject.connectedAzureMonitor = resourceValueToAdd;
    else if (resourceTypeToAdd === 'cloudWatch') updatedProject.connectedCloudWatch = resourceValueToAdd;
    handleModifyProject(updatedProject);
    setAddResourceDialogOpen(false);
    setResourceTypeToAdd('');
    setResourceValueToAdd('');
  };

  const renderHomePlanBoard = () => {
    return <Alert severity="info">The detailed plan board view will be shown here.</Alert>;
  };

  const renderComingSoon = (title: string, icon: React.ReactNode, description: string) => (
    <Box sx={{ textAlign: 'center', mt: 8, p: 3 }}>
      <Avatar sx={{ bgcolor: 'primary.light', width: 56, height: 56, margin: '0 auto 16px' }}>
        {icon}
      </Avatar>
      <Typography variant="h5" gutterBottom>{title}</Typography>
      <Typography color="text.secondary">{description}</Typography>
      <Typography color="text.secondary" sx={{ mt: 1 }}>This feature is under development and will be available soon.</Typography>
    </Box>
  );

  const renderManageProjectPage = () => {
    if (!selectedProjectData) {
      return <Alert severity="warning">No project selected. Please select a project from the Home page.</Alert>;
    }
    return (
      <Box>
        <Typography variant="h4" gutterBottom>{selectedProjectData.name}</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Details</Typography>
                <Typography variant="body1"><strong>Project ID:</strong> {selectedProjectData.id}</Typography>
                <Typography variant="body1"><strong>Manager:</strong> {selectedProjectData.manager}</Typography>
                <Typography variant="body1"><strong>Start Date:</strong> {selectedProjectData.startDate}</Typography>
                <Typography variant="body1"><strong>End Date:</strong> {selectedProjectData.endDate}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Team Members</Typography>
                <Stack direction="row" spacing={1}>
                  {selectedProjectData.team.map(member => <Chip key={member} label={member} />)}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" gutterBottom>Connected Resources</Typography>
                  <Button startIcon={<AddLinkIcon />} onClick={() => setAddResourceDialogOpen(true)}>Add Resource</Button>
                </Box>
                <List>
                  {selectedProjectData.connectedBoard && <ListItem secondaryAction={<IconButton edge="end" aria-label="delete" onClick={() => handleOpenRemoveDialog('board')}><DeleteIcon /></IconButton>}><ListItemIcon><ViewKanbanIcon /></ListItemIcon><ListItemText primary="Project Board" secondary={selectedProjectData.connectedBoard} /></ListItem>}
                  {selectedProjectData.connectedGithub && <ListItem secondaryAction={<IconButton edge="end" aria-label="delete" onClick={() => handleOpenRemoveDialog('github')}><DeleteIcon /></IconButton>}><ListItemIcon><GitHubIcon /></ListItemIcon><ListItemText primary="GitHub Repository" secondary={selectedProjectData.connectedGithub} /></ListItem>}
                  {selectedProjectData.connectedStorage && <ListItem secondaryAction={<IconButton edge="end" aria-label="delete" onClick={() => handleOpenRemoveDialog('storage')}><DeleteIcon /></IconButton>}><ListItemIcon><StorageIcon /></ListItemIcon><ListItemText primary="Cloud Storage" secondary={selectedProjectData.connectedStorage} /></ListItem>}
                  {selectedProjectData.connectedDatabase && <ListItem secondaryAction={<IconButton edge="end" aria-label="delete" onClick={() => handleOpenRemoveDialog('database')}><DeleteIcon /></IconButton>}><ListItemIcon><StorageIcon /></ListItemIcon><ListItemText primary="Database Connection" secondary={selectedProjectData.connectedDatabase} /></ListItem>}
                  {selectedProjectData.connectedCloudLogging && <ListItem secondaryAction={<IconButton edge="end" aria-label="delete" onClick={() => handleOpenRemoveDialog('cloudLogging')}><DeleteIcon /></IconButton>}><ListItemIcon><CloudIcon /></ListItemIcon><ListItemText primary="Google Cloud Logging" secondary={selectedProjectData.connectedCloudLogging} /></ListItem>}
                  {selectedProjectData.connectedAzureMonitor && <ListItem secondaryAction={<IconButton edge="end" aria-label="delete" onClick={() => handleOpenRemoveDialog('azureMonitor')}><DeleteIcon /></IconButton>}><ListItemIcon><CloudIcon /></ListItemIcon><ListItemText primary="Azure Monitor Logs" secondary={selectedProjectData.connectedAzureMonitor} /></ListItem>}
                  {selectedProjectData.connectedCloudWatch && <ListItem secondaryAction={<IconButton edge="end" aria-label="delete" onClick={() => handleOpenRemoveDialog('cloudWatch')}><DeleteIcon /></IconButton>}><ListItemIcon><CloudIcon /></ListItemIcon><ListItemText primary="AWS CloudWatch Logs" secondary={selectedProjectData.connectedCloudWatch} /></ListItem>}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const NoProjectSelectedForPlan = ({ onSelectClick, onNewClick }: { onSelectClick: (event: React.MouseEvent<HTMLButtonElement>) => void, onNewClick: () => void }) => (
    <Paper sx={{ textAlign: 'center', p: 4, mt: 4 }}>
      <Typography variant="h6" gutterBottom>No Project Selected</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Please select a project to view its plan, or create a new one to get started.
      </Typography>
      <Stack direction="row" spacing={2} justifyContent="center">
        <Button variant="contained" onClick={onSelectClick}>
          Select a Project
        </Button>
        <Button variant="outlined" onClick={onNewClick}>
          Create a New Plan
        </Button>
      </Stack>
    </Paper>
  );

  const renderPlanPage = () => {
    if (!selectedProject) {
      return <NoProjectSelectedForPlan
        onSelectClick={handleProjectSelectClick}
        onNewClick={() => setSelectedSidebarItem('newProject')}
      />;
    }

    if (!selectedProjectData) {
      return <Paper sx={{p: 4, textAlign: 'center'}}><CircularProgress /></Paper>;
    }

    const componentsToRender = isPlanEditing && editedPlanComponents
        ? editedPlanComponents
        : selectedProjectData.planComponents;

    if (!componentsToRender || componentsToRender.length === 0) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6">No Plan Components Found</Typography>
                <Typography color="text.secondary" sx={{my: 1}}>
                    This project does not have any plan components to display.
                </Typography>
                <Button sx={{mt: 2}} variant="contained" onClick={() => setSelectedSidebarItem('newProject')}>
                    Create a New Plan
                </Button>
            </Paper>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" gutterBottom>Plan for: {selectedProjectData.name}</Typography>
                <Stack direction="row" spacing={2}>
                    {isPlanEditing ? (
                        <>
                            <Button variant="outlined" onClick={handleCancelPlanEdit}>Cancel</Button>
                            <Button variant="contained" color="primary" onClick={handleSavePlan}>Save Plan</Button>
                        </>
                    ) : (
                        <Button variant="contained" startIcon={<EditIcon />} onClick={handleEditPlan}>Edit Plan</Button>
                    )}
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => setSelectedSidebarItem('home')}>Back to Home</Button>
                </Stack>
            </Box>
            <TableContainer component={Paper}>
                <Table aria-label="project plan table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Summary</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Targets</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Assignee</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {componentsToRender.map((component, index) => (
                            <TableRow key={component.id}>
                                <TableCell component="th" scope="row">{component.name}</TableCell>
                                <TableCell>
                                    <FormControl size="small" fullWidth disabled={!isPlanEditing}>
                                        <Select native value={component.status} onChange={(e) => handlePlanComponentChange(index, 'status', e.target.value)}>
                                            <option>Under Review</option>
                                            <option>Approved</option>
                                            <option>Reevaluation requested</option>
                                            <option>Executed</option>
                                            <option>Hold</option>
                                            <option>Rejected</option>
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <FormControl size="small" fullWidth disabled={!isPlanEditing}>
                                        <Select native value={component.target} onChange={(e) => handlePlanComponentChange(index, 'target', e.target.value)}>
                                            <option>Outsource contract</option>
                                            <option>Staffing</option>
                                            <option>Leadership signoff</option>
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <FormControl size="small" fullWidth disabled={!isPlanEditing}>
                                        <Select native value={component.assignee} onChange={(e) => handlePlanComponentChange(index, 'assignee', e.target.value)}>
                                            {teamMembers.map(m => <option key={m} value={m}>{m}</option>)}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
  };

  const renderContentArea = () => {
    if (!isAppReady) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <Typography variant="h5" color="text.secondary">Loading application data...</Typography>
        </Box>
      );
    }
    switch (selectedSidebarItem) {
      case 'home':
        return (
          <Box>
            {showHomePlanBoard ? (
              renderHomePlanBoard()
            ) : (
              <>
                <Paper sx={{ p: 3, mb: 3, mt: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 0 }}>Project overview</Typography>
                    <>
                      <Button variant="contained" onClick={handleProjectSelectClick} endIcon={<ArrowDropDownIcon />}>
                        {projectList.find(p => p.id === selectedProject)?.name || 'Select a project'}
                      </Button>
                      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleProjectSelectClose}>
                        {projectList.length > 0 ? (
                          projectList.map((project) => (
                            <MenuItem key={project.id} onClick={() => handleProjectMenuItemClick(project.id)}>
                              {project.name}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>No projects found.</MenuItem>
                        )}
                      </Menu>
                    </>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: (theme) => theme.shadows[4] } }} onClick={() => setSelectedSidebarItem('plan')}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'secondary.light' }}><DescriptionIcon /></Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>Plan</Typography>
                            <Typography variant="body2" color="text.secondary">Review and manage the detailed project plan.</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: (theme) => theme.shadows[4] } }} onClick={() => setSelectedSidebarItem('develop')}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'info.light' }}><RocketLaunchIcon /></Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>Develop</Typography>
                            <Typography variant="body2" color="text.secondary">Connect to GitHub and analyze repository activity.</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'warning.light' }}><SupportIcon /></Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>Sustain</Typography>
                            <Typography variant="body2" color="text.secondary">Coming Soon</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: (theme) => theme.shadows[4] } }} onClick={() => { if (selectedProject) setSelectedSidebarItem('manageProject')}}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">Manage Project</Typography>
                          <IconButton size="small" disabled={!selectedProject}><EditIcon /></IconButton>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {selectedProject ? `View and edit details for ${projectList.find(p => p.id === selectedProject)?.name}.` : 'Select a project to manage its details.'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}><RiskRadar risks={risks} /></Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: (theme) => theme.shadows[4] } }} onClick={() => setSelectedSidebarItem('collab')}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">Collab Space</Typography>
                          <IconButton size="small"><MoreHorizIcon /></IconButton>
                        </Box>
                        <List disablePadding>
                          <ListItem disablePadding sx={{ py: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <GroupIcon sx={{ color: 'text.secondary' }} />
                              <Box>
                                <Typography variant="body2">Knowledge Base</Typography>
                                {knowledgeItems.length > 0 ? (
                                  <Typography variant="caption" color="text.secondary">Last updated: {new Date(knowledgeItems[0].createdAt).toLocaleString()}</Typography>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">No items yet</Typography>
                                )}
                              </Box>
                            </Box>
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        );
      case 'plan': return renderPlanPage();
      case 'manageProject': return renderManageProjectPage();
      case 'newProject': return <NewProject onGoBack={() => setSelectedSidebarItem('home')} onProjectCreate={handleCreateProject} planMode={newProjectMode} setPlanMode={setNewProjectMode} totalAutomationResult={totalAutomationResult} setTotalAutomationResult={setTotalAutomationResult} generatedProjectName={generatedProjectName} setGeneratedProjectName={setGeneratedProjectName} />;
      case 'projects': return <ProjectsList projects={userProjects} onNavigateToNewProject={() => setSelectedSidebarItem('newProject')} onDeleteProject={handleDeleteProject} onModifyProject={handleModifyProject} onSelectProject={(projectId: number) => { setSelectedProject(projectId); setSelectedSidebarItem('home'); setShowHomePlanBoard(true); }} />;
      case 'develop': return <Develop developState={developState} setDevelopState={setDevelopState} onGoBack={() => setSelectedSidebarItem('home')} onResetState={resetDevelopState} />;
      case 'dashboards': return renderComingSoon('Custom Dashboards', <DashboardIcon />, 'Create and customize dashboards to visualize key project metrics.');
      case 'boards': return <BoardsPage projects={userProjects} />;
      case 'settings': return <SettingsPage />;
      case 'collab': return <CollabSpace knowledgeItems={knowledgeItems} onAddKnowledgeItem={handleAddKnowledgeItem} onFetchKnowledgeItems={fetchKnowledgeItems} userName={userName} setUserName={setUserName} onGoBack={() => setSelectedSidebarItem('home')} />;
      default: return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar selectedItem={selectedSidebarItem} onSelectItem={setSelectedSidebarItem} />
      <Box component="main" sx={{ flexGrow: 1, width: `calc(100% - ${drawerWidth}px)` }}>
        <Box sx={{
          position: 'fixed', top: 0, left: drawerWidth, width: `calc(100% - ${drawerWidth}px)`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          height: '64px', px: 2, bgcolor: 'background.paper',
          borderBottom: 1, borderColor: 'divider', zIndex: (theme) => theme.zIndex.drawer + 1,
        }}>
          <Tooltip title="Customize in Settings" arrow disableHoverListener={companyName !== DEFAULT_COMPANY_NAME}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{companyName}</Typography>
          </Tooltip>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title="Customize in Settings" arrow disableHoverListener={teamName !== DEFAULT_TEAM_NAME}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{teamName}</Typography>
            </Tooltip>
            <Tooltip title="Toggle light/dark theme" arrow>
              <IconButton onClick={toggleColorMode} color="inherit">
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>
            <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={() => setInviteDialogOpen(true)}>
              Invite
            </Button>
          </Box>
        </Box>
        <Toolbar />
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {message && (
            <Alert
                severity={message.severity}
                onClose={() => setMessage(null)}
                sx={{ mb: 2 }}
            >
                {message.text}
            </Alert>
          )}
          {renderContentArea()}
        </Container>
      </Box>

      <Dialog open={isInviteDialogOpen} onClose={() => setInviteDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Invite Team Members</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter email addresses separated by commas. They will be added to the team roster and can be assigned tasks.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="emails"
            label="Email Addresses"
            type="email"
            fullWidth
            variant="standard"
            value={inviteEmails}
            onChange={(e) => setInviteEmails(e.target.value)}
            placeholder="user1@example.com, user2@example.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleInviteSend} variant="contained">Send Invites</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isAddResourceDialogOpen} onClose={() => setAddResourceDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add a New Resource</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select the type of resource you want to connect to this project.
          </DialogContentText>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="resource-type-label">Resource Type</InputLabel>
            <Select
              labelId="resource-type-label"
              value={resourceTypeToAdd}
              label="Resource Type"
              onChange={(e) => setResourceTypeToAdd(e.target.value as any)}
            >
              <MenuItem value="board">Project Board</MenuItem>
              <MenuItem value="github">GitHub Repository</MenuItem>
              <MenuItem value="storage">Cloud Storage</MenuItem>
              <MenuItem value="database">Database Connection</MenuItem>
              <MenuItem value="cloudLogging">Google Cloud Logging</MenuItem>
              <MenuItem value="azureMonitor">Azure Monitor Logs</MenuItem>
              <MenuItem value="cloudWatch">AWS CloudWatch Logs</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Resource Link or Name"
            value={resourceValueToAdd}
            onChange={(e) => setResourceValueToAdd(e.target.value)}
            disabled={!resourceTypeToAdd}
            placeholder={
              resourceTypeToAdd === 'github' ? 'e.g., https://github.com/owner/repo' :
              resourceTypeToAdd === 'database' ? 'e.g., user:pass@host:port/dbname' :
              'Enter the value'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddResourceDialogOpen(false); setResourceTypeToAdd(''); setResourceValueToAdd(''); }}>Cancel</Button>
          <Button onClick={handleSaveResource} variant="contained" disabled={!resourceTypeToAdd || !resourceValueToAdd}>Add Resource</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isRemoveResourceDialogOpen}
        onClose={() => setRemoveResourceDialogOpen(false)}
      >
        <DialogTitle>Confirm Resource Removal</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            <AlertTitle>Warning</AlertTitle>
            Are you sure you want to remove this resource connection? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveResourceDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmRemoveResource} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function ThemeWrapper() {
  const { mode } = useAppContext();

  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      ...(mode === 'light'
        ? {
            primary: { main: '#0d6efd' },
            secondary: { main: '#6c757d' },
            background: { default: '#f8f9fa', paper: '#ffffff' },
            text: { primary: '#212529', secondary: '#6c757d' },
            divider: 'rgba(0, 0, 0, 0.08)',
          }
        : {
            primary: { main: '#64b5f6' },
            secondary: { main: '#adb5bd' },
            background: { default: '#121821', paper: '#1a2027' },
            text: { primary: '#f8f9fa', secondary: '#adb5bd' },
            divider: 'rgba(255, 255, 255, 0.12)',
          }),
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h4: { fontWeight: 700 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiTypography: {
        defaultProps: {
          color: 'text.primary',
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          containedPrimary: {
            boxShadow: `0 4px 14px 0 ${alpha('#0d6efd', 0.25)}`,
            '&:hover': {
              boxShadow: `0 6px 20px 0 ${alpha('#0d6efd', 0.3)}`,
            }
          }
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.15)'}`,
            boxShadow: mode === 'light'
              ? '0 1px 2px rgba(0,0,0,0.02), 0 2px 4px rgba(0,0,0,0.02), 0 4px 8px rgba(0,0,0,0.02), 0 8px 16px rgba(0,0,0,0.02)'
              : '0 1px 2px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.1), 0 4px 8px rgba(0,0,0,0.1), 0 8px 16px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === 'dark' ? '#000' : '#fff',
            color: mode === 'dark' ? '#fff' : '#000',
            border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
          },
          arrow: {
            color: mode === 'dark' ? '#000' : '#fff',
          }
        }
      }
    },
  }), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppContent />
    </ThemeProvider>
  );
}

function App() {
  return (
    <AppProvider>
      <ThemeWrapper />
    </AppProvider>
  );
}

export default App;